import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { notificationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Bell, MessageSquare, UserPlus, UserMinus, ArrowRight, AtSign, CheckCircle2, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const KIND_META = {
  note_added: { icon: MessageSquare, color: 'text-blue-600 bg-blue-50' },
  subtask_assigned: { icon: UserPlus, color: 'text-green-600 bg-green-50' },
  cell_assigned: { icon: UserPlus, color: 'text-green-600 bg-green-50' },
  status_changed: { icon: CheckCircle2, color: 'text-indigo-600 bg-indigo-50' },
  client_added: { icon: UserPlus, color: 'text-emerald-600 bg-emerald-50' },
  client_removed: { icon: UserMinus, color: 'text-rose-600 bg-rose-50' },
  mention: { icon: AtSign, color: 'text-purple-600 bg-purple-50' },
  overdue_digest: { icon: Clock, color: 'text-amber-600 bg-amber-50' },
  system: { icon: Bell, color: 'text-gray-600 bg-gray-50' },
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.list({ unread_only: unreadOnly ? 'true' : undefined, limit: 100 });
      setNotifications(res.data?.items || []);
    } catch (err) {
      logger.error('Failed to load notifications', err);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const handleRowClick = async (n) => {
    try {
      if (!n.is_read) await notificationsAPI.markRead(n.id);
    } catch (err) {
      logger.warn('mark-read failed (non-blocking)', err);
    }
    if (n.link) navigate(n.link);
    else load();
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await notificationsAPI.markAllRead();
      toast.success(`Marked ${res.data?.marked || 0} notification(s) as read`);
      load();
    } catch (err) { toast.error('Failed to mark all read'); }
  };

  const handleDelete = async (e, n) => {
    e.stopPropagation();
    try {
      await notificationsAPI.remove(n.id);
      setNotifications(prev => prev.filter(x => x.id !== n.id));
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleClearRead = async () => {
    if (!window.confirm('Delete all read notifications older than 7 days?')) return;
    try {
      const res = await notificationsAPI.clearRead();
      toast.success(`Deleted ${res.data?.deleted || 0} old notifications`);
      load();
    } catch (err) { toast.error('Failed to clear'); }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="page-shell space-y-6 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="notifications-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recent activity across your assigned subtasks and clients.
            {unreadCount > 0 && <span className="ml-2"><Badge variant="default">{unreadCount} unread</Badge></span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={unreadOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUnreadOnly(v => !v)}
            data-testid="filter-unread-only"
          >
            {unreadOnly ? 'Showing unread' : 'Show unread only'}
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read">
              Mark all read
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleClearRead} data-testid="clear-old" className="text-muted-foreground">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear old
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && notifications.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
          ) : notifications.length === 0 ? (
            <div className="py-16 text-center">
              <Bell className="h-10 w-10 mx-auto text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground mt-3">
                {unreadOnly ? 'No unread notifications — you\'re all caught up!' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map(n => {
                const meta = KIND_META[n.kind] || KIND_META.system;
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleRowClick(n)}
                    className={`w-full text-left p-4 hover:bg-muted/40 transition-colors flex gap-3 ${!n.is_read ? 'bg-accent/30' : ''}`}
                    data-testid={`notification-row-${n.id}`}
                  >
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground">{n.title}</p>
                        {!n.is_read && <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                      </div>
                      {n.body && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.body}</p>}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[11px] text-muted-foreground">
                          {n.actor_name ? `by ${n.actor_name} · ` : ''}
                          {new Date(n.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {n.link && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, n)}
                      className="text-muted-foreground/50 hover:text-rose-600 p-1"
                      title="Delete"
                      data-testid={`notification-delete-${n.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
